/**
 * ResetPasswordForm Component
 *
 * Form for resetting password with token from email link.
 * Follows the same design patterns as ForgotPasswordForm for consistency.
 *
 * @module components/auth/ResetPasswordForm
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { cn } from '@/utils/cn';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { useTheme } from '@/hooks/useTheme';
import { getAuthService } from '@/services/auth/AuthServiceFactory';
import type { ResetPasswordFormData } from '@/types/auth';
import { getLogger } from '@/services/shared/LoggerFactory';

/**
 * ResetPasswordForm component props interface
 */
interface ResetPasswordFormProps {
  /** Success callback after successful password reset */
  onSuccess?: () => void;
  /** Callback to switch back to login mode */
  onBackToLogin?: () => void;
  /** Custom class name */
  className?: string;
}

/**
 * ResetPasswordForm component for password reset
 *
 * @example
 * ```tsx
 * <ResetPasswordForm
 *   onSuccess={handleResetSuccess}
 *   onBackToLogin={handleBackToLogin}
 * />
 * ```
 */
export function ResetPasswordForm({ onSuccess, onBackToLogin, className = '' }: ResetPasswordFormProps) {
  const logger = getLogger();
  const { isDark } = useTheme();
  const [searchParams] = useSearchParams();
  const authService = getAuthService();

  // Extract token from URL parameters
  const token = searchParams.get('token') || '';

  // Form state
  const [formData, setFormData] = useState<ResetPasswordFormData>({
    password: '',
    confirmPassword: '',
    token: token,
  });

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Field-level errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});

  // Update token when URL params change
  useEffect(() => {
    setFormData(prev => ({ ...prev, token }));
  }, [token]);

  /**
   * Handle field value changes
   */
  const handleFieldChange = useCallback((field: keyof ResetPasswordFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Clear global error
    if (error) {
      setError(null);
    }
  }, [fieldErrors, error]);

  /**
   * Validate password field
   */
  const validatePassword = useCallback((password: string): string => {
    if (!password) {
      return 'Password is required';
    }
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Password must contain at least one number';
    }
    return '';
  }, []);

  /**
   * Validate all form fields
   */
  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    // Token validation
    if (!formData.token) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
      return false;
    }

    // Password validation
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      errors.password = passwordError;
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFieldErrors(errors);

    // Mark all fields as touched to show errors
    setTouchedFields({
      password: true,
      confirmPassword: true,
      token: true
    });

    return Object.keys(errors).length === 0;
  }, [formData, validatePassword]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLoading) return;

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.submitNewPassword(formData.token, formData.password);

      if (response.status === 'OK') {
        setIsSubmitted(true);
        onSuccess?.();
      } else if (response.status === 'RESET_PASSWORD_INVALID_TOKEN_ERROR') {
        setError('Invalid or expired reset token. Please request a new password reset link.');
      } else if (response.status === 'FIELD_ERROR') {
        // Handle field-level validation errors from the service
        if (response.formFields) {
          const errors: Record<string, string> = {};
          response.formFields.forEach(field => {
            errors[field.id] = field.error;
          });
          setFieldErrors(errors);
        } else {
          setError('Invalid password. Please check your password and try again.');
        }
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } catch (error) {
      logger.error('Password reset error:', error);
      setError('Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, validateForm, formData, onSuccess]);

  /**
   * Get field validation props
   */
  const getFieldValidation = useCallback((fieldName: keyof ResetPasswordFormData) => {
    const shouldShowError = touchedFields[fieldName] && fieldErrors[fieldName];

    return {
      error: shouldShowError ? fieldErrors[fieldName] : undefined
    };
  }, [touchedFields, fieldErrors]);

  // Show success message after submission
  if (isSubmitted) {
    return (
      <div className={cn('w-full', className)}>
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className={cn(
            "w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center",
            isDark ? "bg-green-900/20" : "bg-green-50"
          )}>
            <svg className={cn(
              "w-8 h-8",
              isDark ? "text-green-400" : "text-green-600"
            )} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className={cn(
            "text-2xl font-bold mb-2",
            isDark ? "text-secondary-100" : "text-secondary-900"
          )}>
            Password Reset Successful
          </h1>
          <p className={cn(
            isDark ? "text-secondary-400" : "text-secondary-600"
          )}>
            Your password has been successfully reset. You can now sign in with your new password.
          </p>
        </div>

        {/* Back to Login */}
        {onBackToLogin && (
          <div className="text-center">
            <Button
              type="button"
              onClick={onBackToLogin}
              variant="primary"
              size="md"
              className="w-full"
            >
              Continue to Login
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Show error if no token
  if (!token) {
    return (
      <div className={cn('w-full', className)}>
        <div className="text-center mb-8">
          <div className={cn(
            "w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center",
            isDark ? "bg-red-900/20" : "bg-red-50"
          )}>
            <svg className={cn(
              "w-8 h-8",
              isDark ? "text-red-400" : "text-red-600"
            )} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <h1 className={cn(
            "text-2xl font-bold mb-2",
            isDark ? "text-secondary-100" : "text-secondary-900"
          )}>
            Invalid Reset Link
          </h1>
          <p className={cn(
            isDark ? "text-secondary-400" : "text-secondary-600"
          )}>
            This password reset link is invalid or has expired. Please request a new one.
          </p>
        </div>

        {/* Back to Login */}
        {onBackToLogin && (
          <div className="text-center">
            <button
              type="button"
              onClick={onBackToLogin}
              className={cn(
                "font-medium hover:underline",
                isDark ? "text-primary-400" : "text-primary-600"
              )}
            >
              ← Back to Login
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn('w-full', className)}>
      {/* Form Header */}
      <div className="text-left mb-8">
        <h1 className={cn(
          "text-2xl font-bold mb-2",
          isDark ? "text-secondary-100" : "text-secondary-900"
        )}>
          Set New Password
        </h1>
        <p className={cn(
          isDark ? "text-secondary-400" : "text-secondary-600"
        )}>
          Please enter your new password below
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6" noValidate data-testid="reset-password-form">
        {/* Password Field */}
        <FormField
          id="password"
          label="New Password"
          required
          {...getFieldValidation('password')}
        >
          <Input
            type="password"
            value={formData.password}
            onChange={(e) => handleFieldChange('password', e.target.value)}
            placeholder="Enter your new password"
            autoComplete="new-password"
            disabled={isLoading}
            required
            fullWidth
            data-testid="password-input"
          />
        </FormField>

        {/* Confirm Password Field */}
        <FormField
          id="confirmPassword"
          label="Confirm New Password"
          required
          {...getFieldValidation('confirmPassword')}
        >
          <Input
            type="password"
            value={formData.confirmPassword}
            onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
            placeholder="Confirm your new password"
            autoComplete="new-password"
            disabled={isLoading}
            required
            fullWidth
            data-testid="confirm-password-input"
          />
        </FormField>

        {/* Global Error */}
        {error && (
          <div className={cn(
            "p-4 border rounded-lg",
            isDark ? "bg-red-900/20 border-red-800" : "bg-red-50 border-red-200"
          )}>
            <div className="flex items-start">
              <svg className={cn(
                "w-5 h-5 mt-0.5 mr-3 flex-shrink-0",
                isDark ? "text-red-400" : "text-red-600"
              )} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className={cn(
                  "text-sm font-medium",
                  isDark ? "text-red-200" : "text-red-800"
                )}>
                  Reset Failed
                </h3>
                <p className={cn(
                  "text-sm mt-1",
                  isDark ? "text-red-300" : "text-red-700"
                )}>
                  {error}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="primary"
          size="md"
          loading={isLoading}
          disabled={isLoading}
          className="w-full"
          data-testid="reset-password-button"
        >
          {isLoading ? 'Resetting Password...' : 'Reset Password'}
        </Button>

        {/* Back to Login */}
        {onBackToLogin && (
          <div className="text-center">
            <p className={cn(
              "text-sm",
              isDark ? "text-secondary-400" : "text-secondary-600"
            )}>
              Remember your password?{' '}
              <button
                type="button"
                onClick={onBackToLogin}
                disabled={isLoading}
                className={cn(
                  "font-medium hover:underline",
                  isDark ? "text-primary-400" : "text-primary-600",
                  isLoading && "opacity-50 cursor-not-allowed"
                )}
              >
                Back to Login
              </button>
            </p>
          </div>
        )}
      </form>
    </div>
  );
}